import React, { useState, useRef, useEffect } from 'react';

import { Input, useT } from '@repo/common';
import PostSection from '../PostSection';
import usePlaceholder from '../usePlaceholder';
import WorkingScheduleList from './WorkingScheduleList';
import OfficeDaysList from './OfficeDaysList';
import SalaryTypeList from './SalaryTypeList';
import CurrencyList from './CurrencyList';

import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/16px/icon_16px_info_outline.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-top.svg';

const Conditions = ({ textValues, setTextValues, errors }) => {
  const t = useT();
  const ph = usePlaceholder();
  const [isWorkingScheduleOpen, setIsWorkingScheduleOpen] = useState(false);
  const [isOfficeDaysOpen, setIsOfficeDaysOpen] = useState(false);
  const [isSalaryTypeOpen, setIsSalaryTypeOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);

  const refs = useRef({
    workingSchedule: null,
    officeDays: null,
    salaryType: null,
    currency: null,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        refs.current.workingSchedule &&
        !refs.current.workingSchedule.contains(event.target)
      ) {
        setIsWorkingScheduleOpen(false);
      }

      if (
        refs.current.officeDays &&
        !refs.current.officeDays.contains(event.target)
      ) {
        setIsOfficeDaysOpen(false);
      }

      if (
        refs.current.salaryType &&
        !refs.current.salaryType.contains(event.target)
      ) {
        setIsSalaryTypeOpen(false);
      }

      if (
        refs.current.currency &&
        !refs.current.currency.contains(event.target)
      ) {
        setIsCurrencyOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /** 급여 안내 문구. Input의 message 자리에 넣어 필드에 붙여 보여준다 */
  const salaryNotice = (
    <span className='inline-flex items-start gap-0.5 text-grayscale-gray04'>
      <InfoIcon className='mt-[3px] h-4 w-4 shrink-0 text-icon-placeholder' />
      {t(
        'Salary information is used for statistical purposes only and will never be disclosed.',
        '급여 정보는 통계 목적으로만 사용되며 외부에 공개되지 않습니다.',
      )}
    </span>
  );

  const handleTextValueChange = (fieldName) => (e) => {
    setTextValues((prev) => ({
      ...prev,
      [fieldName]: e.target.value,
    }));
  };

  useEffect(() => {
    if (textValues.workingSchedule !== 'Hybrid') {
      setTextValues((prev) => ({
        ...prev,
        officeDays: '',
      }));
    }
  }, [textValues.workingSchedule, setTextValues]);

  return (
    <PostSection title={t('Conditions', '근무 조건')}>
      <div
        ref={(el) => (refs.current.workingSchedule = el)}
        className='relative'
      >
        <Input
          data-validation-field='workingSchedule'
          label={t('Working Schedule*', '근무 형태*')}
          value={
            textValues.workingSchedule === 'Select work schedule'
              ? ''
              : textValues.workingSchedule
          }
          state={errors.workingSchedule ? 'failed' : 'default'}
          showStatusIcon={false}
          message={errors.workingSchedule}
          placeholder={ph(t('Select work schedule', '근무 형태를 선택하세요'))}
          rightIcon={
            isWorkingScheduleOpen ? (
              <ArrowTopIcon className='text-icon-placeholder' />
            ) : (
              <ArrowBottomIcon className='text-icon-placeholder' />
            )
          }
          readOnly
          onClick={() => setIsWorkingScheduleOpen((prev) => !prev)}
          onRightIconClick={() => setIsWorkingScheduleOpen((prev) => !prev)}
        />

        {isWorkingScheduleOpen && (
          <div className='absolute top-full mt-1 left-0 z-20 w-full'>
            <WorkingScheduleList
              selectedOption={textValues.workingSchedule}
              onSelect={(option) => {
                setTextValues((prev) => ({
                  ...prev,
                  workingSchedule: option,
                }));
                setIsWorkingScheduleOpen(false);
              }}
            />
          </div>
        )}

        {/* 모바일에서는 카드 배경 없이 일반 입력 항목처럼 보이게 한다 */}
        {textValues.workingSchedule === 'Hybrid' && (
          <div
            ref={(el) => (refs.current.officeDays = el)}
            className='relative mt-2 flex w-full flex-col items-start gap-2 tabletH:rounded-lg tabletH:bg-surface-lightgray60 tabletH:p-4'
          >
            <div className='typo-b14Strong text-text-primary tabletH:typo-b14 tabletH:text-text-tertiary'>
              {t('Office days per week (optional)', '주간 출근 일수 (선택)')}
            </div>
            <Input
              data-validation-field='officeDays'
              className='w-full'
              label=''
              value={
                textValues.officeDays === 'Select office days per week'
                  ? ''
                  : textValues.officeDays
              }
              placeholder={ph(
                t('Select office days per week', '주간 출근 일수를 선택하세요'),
              )}
              state={errors.officeDays ? 'failed' : 'default'}
              showStatusIcon={false}
              message={errors.officeDays}
              rightIcon={
                isOfficeDaysOpen ? (
                  <ArrowTopIcon className='text-icon-placeholder' />
                ) : (
                  <ArrowBottomIcon className='text-icon-placeholder' />
                )
              }
              onClick={() => setIsOfficeDaysOpen((prev) => !prev)}
              onRightIconClick={() => setIsOfficeDaysOpen((prev) => !prev)}
            />

            {isOfficeDaysOpen && (
              <div className='absolute left-0 top-full z-20 mt-1 w-full tabletH:-mt-3 tabletH:px-4'>
                <OfficeDaysList
                  selectedOption={textValues.officeDays}
                  onSelect={(option) => {
                    setTextValues((prev) => ({
                      ...prev,
                      officeDays: option,
                    }));
                    setIsOfficeDaysOpen(false);
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <Input
        label={t('Work Time (optional)', '근무 시간 (선택)')}
        placeholder={t('e.g. 09:00 - 18:00', '예: 09:00 - 18:00')}
        value={textValues.workTime}
        onChange={handleTextValueChange('workTime')}
        maxLength={100}
      />

      <Input
        label={t('Working Days (optional)', '근무 요일 (선택)')}
        placeholder={t('e.g. Monday - Friday', '예: 월요일 - 금요일')}
        value={textValues.workingDays}
        onChange={handleTextValueChange('workingDays')}
        maxLength={100}
      />

      <div ref={(el) => (refs.current.salaryType = el)} className='relative'>
        <Input
          data-validation-field='salaryType'
          label={t('Salary type (optional)', '급여 유형 (선택)')}
          value={
            textValues.salaryType === 'Select salary type'
              ? ''
              : textValues.salaryType
          }
          state={errors.salaryType ? 'failed' : 'default'}
          showStatusIcon={false}
          message={errors.salaryType}
          placeholder={ph(t('Select salary type', '급여 유형을 선택하세요'))}
          rightIcon={
            isSalaryTypeOpen ? (
              <ArrowTopIcon className='text-icon-placeholder' />
            ) : (
              <ArrowBottomIcon className='text-icon-placeholder' />
            )
          }
          readOnly
          onClick={() => setIsSalaryTypeOpen((prev) => !prev)}
          onRightIconClick={() => setIsSalaryTypeOpen((prev) => !prev)}
        />

        {isSalaryTypeOpen && (
          <div className='absolute top-full mt-1 left-0 z-20 w-full'>
            <SalaryTypeList
              selectedOption={textValues.salaryType}
              onSelect={(option) => {
                setTextValues((prev) => ({
                  ...prev,
                  salaryType: option,
                }));
                setIsSalaryTypeOpen(false);
              }}
            />
          </div>
        )}
      </div>

      <div ref={(el) => (refs.current.currency = el)} className='relative'>
        <Input
          data-validation-field='currency'
          label={t('Currency (optional)', '통화 (선택)')}
          placeholder={ph(t('Select currency', '통화를 선택하세요'))}
          value={textValues.currency}
          state={errors.currency ? 'failed' : 'default'}
          showStatusIcon={false}
          message={errors.currency}
          rightIcon={
            isCurrencyOpen ? (
              <ArrowTopIcon className='text-icon-placeholder' />
            ) : (
              <ArrowBottomIcon className='text-icon-placeholder' />
            )
          }
          readOnly
          onClick={() => setIsCurrencyOpen((prev) => !prev)}
          onRightIconClick={() => setIsCurrencyOpen((prev) => !prev)}
        />
        {isCurrencyOpen && (
          <div className='absolute top-full mt-1 left-0 z-20 w-full'>
            <CurrencyList
              selectedOption={textValues.currency}
              onSelect={(option) => {
                setTextValues((prev) => ({
                  ...prev,
                  currency: option,
                }));
                setIsCurrencyOpen(false);
              }}
            />
          </div>
        )}
      </div>

      <div className='w-full flex flex-col gap-2'>
        <div className='flex items-center gap-0.5'>
          <span className='typo-b12Strong tabletH:typo-b16 text-text-primary'>
            {t('Amount (optional)', '금액 (선택)')}
          </span>
        </div>
        <div className='w-full flex flex-col gap-2 tabletH:flex-row tabletH:items-start'>
          <Input
            data-validation-field='amount'
            className='w-full'
            label=''
            placeholder={
              textValues.currency === 'KRW'
                ? t('e.g. 28,000,000', '예: 28,000,000')
                : t('e.g. 40,000', '예: 40,000')
            }
            value={textValues.minAmount}
            state={errors.amount ? 'failed' : 'default'}
            showStatusIcon={false}
            message={errors.amount}
            onChange={handleTextValueChange('minAmount')}
            numberOnly
          />
          <div className='flex w-full items-center justify-center typo-b20Strong text-text-primary tabletH:h-12 tabletH:w-auto'>
            ~
          </div>
          <Input
            className='w-full'
            label=''
            placeholder={
              textValues.currency === 'KRW'
                ? t('e.g. 65,000,000', '예: 65,000,000')
                : t('e.g. 130,000', '예: 130,000')
            }
            value={textValues.maxAmount}
            state={errors.amount ? 'failed' : 'default'}
            showStatusIcon={false}
            message={errors.amount ? undefined : salaryNotice}
            onChange={handleTextValueChange('maxAmount')}
            numberOnly
          />
        </div>
      </div>
    </PostSection>
  );
};

export default Conditions;
